import { zodResolver } from "@hookform/resolvers/zod";
import { MinusIcon, PlusIcon } from "lucide-react";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import type * as z from "zod";

import { authMapperSchema } from "@blis/shared/auth";
import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import { Menu, MenuItem, MenuTrigger } from "@/components/ui/menu";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/toast";
import type { AuthProvider } from "../../types";
import { Section } from "../Section";
import { TabLayout } from "../TabLayout";

type FormValues = z.infer<typeof authMapperSchema>;

const defaultAttributeOptions = [
  { value: "firstName", label: "firstName" },
  { value: "lastName", label: "lastName" },
  { value: "name", label: "name" },
  { value: "roles", label: "roles" },
  { value: "email", label: "email" },
];

const defaultRoleOptions = [
  { value: "blis_org_readonly", label: "Field tech (read-only)" },
  { value: "blis_org_tech", label: "Field tech" },
  { value: "blis_lab_readonly", label: "Lab tech (read-only)" },
  { value: "blis_lab_assistant", label: "Lab assistant" },
  { value: "blis_lab_tech", label: "Lab tech" },
  { value: "blis_lab_manager", label: "Lab manager" },
  { value: "blis_lab_admin", label: "Admin" },
];

const defaultOperatorOptions = [
  { label: "equals", value: "equals" },
  { label: "contains", value: "contains" },
  { label: "regex", value: "regex" },
];

export function Mappers({ provider }: { provider: AuthProvider }) {
  const methods = useForm({
    // biome-ignore lint/suspicious/noExplicitAny: JSON type
    defaultValues: provider.mapperConfig as any,
    resolver: zodResolver(authMapperSchema),
  });

  const update = trpc.admin.auth.updateMapperConfig.useMutation({
    onSuccess() {
      toast.success("Mapper config saved");
    },
    onError(err) {
      toast.error("Error updating mapper config", {
        description: err.message,
      });
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    await update.mutateAsync({ id: provider.id, patch: values });
  };

  return (
    <TabLayout>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <Section.Subtitle>Attribute mappers</Section.Subtitle>
              <AttributeMappers />
            </div>

            <div className="flex flex-col gap-2">
              <Section.Subtitle>Role mappers</Section.Subtitle>
              <RoleMappers />
            </div>

            <div className="flex flex-col gap-2">
              <Section.Subtitle>Org mappers</Section.Subtitle>
              <OrgMappers />
            </div>

            <div className="flex flex-col gap-2">
              <Section.Subtitle>Allowed org mappers</Section.Subtitle>
              <AllowedOrgsMappers />
            </div>

            <Separator />
            <div className="flex items-center justify-end">
              <Button type="submit" isPending={methods.formState.isSubmitting}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </TabLayout>
  );
}

function AttributeMapper({ index }: { index: number }) {
  const { control } = useFormContext<FormValues>();

  return (
    <>
      <input
        type="hidden"
        name={`attributes.${index}.type`}
        value="attribute"
      />
      <div className="flex-1">
        <Controller
          control={control}
          name={`attributes.${index}.propName`}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <ComboBox
              {...field}
              isDisabled={index === 0}
              selectedKey={field.value}
              onSelectionChange={field.onChange}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <ComboBoxInput ref={ref} />
              <ComboBoxList items={defaultAttributeOptions}>
                {(item) => (
                  <ComboBoxItem id={item.value}>{item.label}</ComboBoxItem>
                )}
              </ComboBoxList>
              <FieldError>{error?.message}</FieldError>
            </ComboBox>
          )}
        />
      </div>
      <div className="flex-1">
        <Controller
          control={control}
          name={`attributes.${index}.attributeName`}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              onChange={(value) => field.onChange(value)}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      </div>
    </>
  );
}

function AttributeMappers() {
  const { control } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "attributes",
  });

  return (
    <>
      {fields.map((field, index) => (
        <div className="flex items-center gap-2" key={field.id}>
          <AttributeMapper index={index} />
          <Button
            type="button"
            isDisabled={index === 0}
            size="icon-sm"
            variant="ghost"
            onPress={() => remove(index)}
          >
            <MinusIcon />
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          variant="outline"
          onPress={() =>
            append({ type: "attribute", propName: "", attributeName: "" })
          }
        >
          <PlusIcon />
          Add attribute mapper
        </Button>
      </div>
    </>
  );
}

function RoleMapper({
  index,
  type,
}: {
  index: number;
  type: "staticRole" | "dynamicRole";
}) {
  const { control } = useFormContext<FormValues>();

  return type === "staticRole" ? (
    <>
      <input type="hidden" name={`roles.${index}.type`} value="staticRole" />
      <Controller
        control={control}
        name={`roles.${index}.role`}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <Select
            placeholder="Role"
            {...field}
            selectedKey={field.value}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Static role</Label>
            <SelectTrigger />
            <SelectList items={defaultRoleOptions}>
              {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
    </>
  ) : type === "dynamicRole" ? (
    <div className="grid grid-cols-5 gap-2">
      <input type="hidden" name={`roles.${index}.type`} value="dynamicRole" />
      <Controller
        control={control}
        name={`roles.${index}.condition.attribute`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
            className="col-span-2"
          >
            <Label>Attribute</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        control={control}
        name={`roles.${index}.condition.operator`}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <Select
            {...field}
            selectedKey={field.value}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Operator</Label>
            <SelectTrigger />
            <SelectList items={defaultOperatorOptions}>
              {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
      <Controller
        control={control}
        name={`roles.${index}.condition.value`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
            className="col-span-2"
          >
            <Label>Value</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        control={control}
        name={`roles.${index}.role`}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <Select
            {...field}
            selectedKey={field.value}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Set role</Label>
            <SelectTrigger />
            <SelectList items={defaultRoleOptions}>
              {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
    </div>
  ) : null;
}

function RoleMappers() {
  const { control } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "roles",
  });

  return (
    <>
      {fields.map((field, index) => (
        <div className="flex items-center gap-2" key={field.id}>
          <div className="flex-1 border p-3 rounded-md">
            <RoleMapper index={index} type={field.type} />
          </div>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            onPress={() => remove(index)}
          >
            <MinusIcon />
          </Button>
        </div>
      ))}
      <div>
        <MenuTrigger>
          <Button variant="outline">
            <PlusIcon /> Add role mapper
          </Button>
          <Menu aria-label="Add role mapper">
            <MenuItem onAction={() => append({ type: "staticRole", role: "" })}>
              <PlusIcon />
              Static role
            </MenuItem>
            <MenuItem
              onAction={() =>
                append({
                  type: "dynamicRole",
                  role: "",
                  condition: { attribute: "", operator: "equals", value: "" },
                })
              }
            >
              <PlusIcon />
              From attribute
            </MenuItem>
          </Menu>
        </MenuTrigger>
      </div>
    </>
  );
}

function OrgMapper({
  index,
  type,
  orgs,
}: {
  index: number;
  type: "staticOrg" | "dynamicOrg";
  orgs: { id: number; name: string }[];
}) {
  const { control } = useFormContext<FormValues>();

  return type === "staticOrg" ? (
    <>
      <input type="hidden" name={`orgs.${index}.type`} value={type} />
      <Controller
        control={control}
        name={`orgs.${index}.org`}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <Select
            {...field}
            placeholder="Select....."
            isInvalid={!!error}
            validationBehavior="aria"
            selectedKey={field.value}
            onSelectionChange={field.onChange}
          >
            <Label>Static org</Label>
            <SelectTrigger />
            <SelectList items={orgs}>
              {(item) => <SelectItem id={item.id}>{item.name}</SelectItem>}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
    </>
  ) : type === "dynamicOrg" ? (
    <div className="grid grid-cols-5 gap-2">
      <input type="hidden" name={`orgs.${index}.type`} value={type} />
      <Controller
        control={control}
        name={`orgs.${index}.condition.attribute`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
            className="col-span-2"
          >
            <Label>Attribute</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />

      <Controller
        control={control}
        name={`orgs.${index}.condition.operator`}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <Select
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
            selectedKey={field.value}
            onSelectionChange={field.onChange}
          >
            <Label>Operator</Label>
            <SelectTrigger />
            <SelectList items={defaultOperatorOptions}>
              {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
      <Controller
        control={control}
        name={`orgs.${index}.condition.value`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            isInvalid={!!error}
            validationBehavior="aria"
            className="col-span-2"
          >
            <Label>Value</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        control={control}
        name={`orgs.${index}.org`}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <Select
            {...field}
            selectedKey={field.value}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            validationBehavior="aria"
            className="col-span-2"
          >
            <Label>Set org</Label>
            <SelectTrigger />
            <SelectList items={orgs}>
              {(item) => <SelectItem id={item.id}>{item.name}</SelectItem>}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
    </div>
  ) : null;
}

function OrgMappers() {
  const { control } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "orgs",
  });
  const { data } = trpc.orgs.list.useQuery();

  const orgs = data || [];

  return (
    <>
      {orgs.length > 0 &&
        fields.map((field, index) => (
          <div className="flex items-center gap-2" key={field.id}>
            <div className="flex-1 border p-3 rounded-md">
              <OrgMapper index={index} type={field.type} orgs={orgs} />
            </div>
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              onPress={() => remove(index)}
            >
              <MinusIcon />
            </Button>
          </div>
        ))}
      <div>
        <MenuTrigger aria-label="Add org mapper">
          <Button variant="outline">
            <PlusIcon data-slot="icon" /> Add org mapper
          </Button>
          <Menu aria-label="Add org mapper">
            <MenuItem
              onAction={() =>
                append({ type: "staticOrg", org: null as unknown as number })
              }
            >
              <PlusIcon data-slot="icon" />
              Static org
            </MenuItem>
            <MenuItem
              onAction={() =>
                append({
                  type: "dynamicOrg",
                  org: null as unknown as number,
                  condition: { attribute: "", operator: "equals", value: "" },
                })
              }
            >
              <PlusIcon data-slot="icon" />
              From attribute
            </MenuItem>
          </Menu>
        </MenuTrigger>
      </div>
    </>
  );
}

function AllowedOrgsMapper({
  index,
  type,
  orgs,
}: {
  index: number;
  type: "staticAllowedOrgs" | "dynamicAllowedOrgs";
  orgs: { id: number; name: string }[];
}) {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<FormValues>();

  return type === "staticAllowedOrgs" ? (
    <>
      <input type="hidden" name={`orgs.${index}.type`} value={type} />
      <Controller
        control={control}
        name={`allowedOrgs.${index}.allowedOrgs`}
        render={({ field }) => (
          <div>TODO</div>
          // <Select
          //   {...field}
          //   isMulti
          //   label="Static allowed orgs"
          //   placeholder="Select....."
          //   options={orgs}
          //   error={errors.orgs?.[index]?.org?.message}
          //   value={orgs.filter((o) => field.value?.includes(o.id))}
          //   onChange={(evt) => field.onChange(evt?.map((o) => o.id) || null)}
          //   getOptionLabel={(o) => o.name}
          //   getOptionValue={(o) => o.id.toString()}
          // />
        )}
      />
    </>
  ) : type === "dynamicAllowedOrgs" ? (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <input type="hidden" name={`allowedOrgs.${index}.type`} value={type} />
        <Controller
          control={control}
          name={`allowedOrgs.${index}.condition.attribute`}
          render={({ field, fieldState: { error } }) => (
            <TextField {...field} isInvalid={!!error} validationBehavior="aria">
              <Label>Attribute</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name={`allowedOrgs.${index}.condition.operator`}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Select
              {...field}
              isInvalid={!!error}
              validationBehavior="aria"
              selectedKey={field.value}
              onSelectionChange={field.onChange}
            >
              <Label>Operator</Label>
              <SelectTrigger />
              <SelectList items={defaultOperatorOptions}>
                {(item) => (
                  <SelectItem id={item.value}>{item.label}</SelectItem>
                )}
              </SelectList>
              <FieldError>{error?.message}</FieldError>
            </Select>
          )}
        />
        <Controller
          control={control}
          name={`allowedOrgs.${index}.condition.value`}
          render={({ field, fieldState: { error } }) => (
            <TextField {...field} isInvalid={!!error} validationBehavior="aria">
              <Label>Value</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      </div>
      <Controller
        control={control}
        name={`allowedOrgs.${index}.allowedOrgs`}
        render={({ field }) => (
          <div>TODO</div>
          // <Select
          //   {...field}
          //   isMulti
          //   label="Set allowed orgs"
          //   options={orgs}
          //   value={orgs.filter((o) => field.value?.includes(o.id))}
          //   onChange={(evt) => field.onChange(evt?.map((o) => o.id) || null)}
          //   getOptionLabel={(o) => o.name}
          //   // biome-ignore lint/suspicious/noExplicitAny: could do toString, but may break something
          //   getOptionValue={(o) => o.id as any}
          //   error={errors.orgs?.[index]?.org?.message}
          // />
        )}
      />
    </div>
  ) : null;
}

function AllowedOrgsMappers() {
  const { control } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "allowedOrgs",
  });
  const { data } = trpc.orgs.list.useQuery();

  const orgs = data || [];

  return (
    <>
      {orgs.length > 0 &&
        fields.map((field, index) => (
          <div className="flex items-center gap-2" key={field.id}>
            <div className="flex-1 border p-3 rounded-md">
              <AllowedOrgsMapper index={index} type={field.type} orgs={orgs} />
            </div>
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              onPress={() => remove(index)}
            >
              <MinusIcon />
            </Button>
          </div>
        ))}
      <div>
        <MenuTrigger>
          <Button variant="outline">
            <PlusIcon data-slot="icon" /> Add org mapper
          </Button>
          <Menu aria-label="Add org mapper">
            <MenuItem
              onAction={() =>
                append({
                  type: "staticAllowedOrgs",
                  allowedOrgs: null as unknown as number[],
                })
              }
            >
              <PlusIcon />
              Static allowed orgs
            </MenuItem>
            <MenuItem
              onAction={() =>
                append({
                  type: "dynamicAllowedOrgs",
                  allowedOrgs: null as unknown as number[],
                  condition: { attribute: "", operator: "equals", value: "" },
                })
              }
            >
              <PlusIcon />
              From attribute
            </MenuItem>
          </Menu>
        </MenuTrigger>
      </div>
    </>
  );
}
